import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, Clock, XCircle, Loader2, ArrowRight, LifeBuoy } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CATEGORIES_ABONNEMENT as CATEGORIES } from "@/lib/categories";
import { getUser, saveUser } from "@/lib/user";
import { RetourVuePrecedente } from "@/lib/retourVue";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CLE_TX = "fodindin_transaction";

const cibleRubrique = (categorie) => {
  if (categorie === "air") return "/air-bengue";
  // Pack Tout-Accès : on entre par FodindinLago, toutes les rubriques sont ouvertes.
  if (categorie === "tout") return "/categorie/lago/annonces";
  return `/categorie/${categorie || "lago"}/annonces`;
};

export default function ResultatPaiement() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // CinetPay ne renvoie pas toujours la référence : on garde la nôtre au départ du paiement.
  const transactionId =
    params.get("transaction_id") ||
    params.get("cpm_trans_id") ||
    params.get("token") ||
    params.get("tx") ||
    localStorage.getItem(CLE_TX);

  const [etat, setEtat] = useState("verification");
  const [categorie, setCategorie] = useState(null);
  // Paiement d'annonce : on emmène le membre DIRECTEMENT sur son annonce publiée.
  const [annonceId, setAnnonceId] = useState(null);
  const [typePaiement, setTypePaiement] = useState(null);
  const [compteur, setCompteur] = useState(2);

  useEffect(() => {
    if (!transactionId) {
      setEtat("introuvable");
      return;
    }
    let annule = false;
    let essais = 0;
    const verifier = async () => {
      try {
        const r = await axios.get(`${API}/paiements/statut/${transactionId}`);
        if (annule) return;
        if (r.data.category) setCategorie(r.data.category);
        if (r.data.type) setTypePaiement(r.data.type);
        if (r.data.annonce_id) setAnnonceId(r.data.annonce_id);
        if (r.data.statut === "accepte") {
          localStorage.removeItem(CLE_TX);
          // Le paiement est souvent réglé depuis le navigateur de l'appli Mobile Money :
          // on recrée la session du payeur pour qu'il entre directement dans sa rubrique.
          const membre = r.data.membre;
          if (membre && getUser()?.id !== membre.id) {
            saveUser({ ...(getUser() || {}), ...membre, telephone_verifie: true });
          }
          return setEtat("accepte");
        }
        if (r.data.statut === "refuse") return setEtat("refuse");
        essais += 1;
        if (essais < 8) setTimeout(verifier, 2500);
        else setEtat("en_attente");
      } catch (err) {
        if (annule) return;
        if (err.response?.status === 404) return setEtat("introuvable");
        essais += 1;
        if (essais < 4) setTimeout(verifier, 3000);
        else setEtat("en_attente");
      }
    };
    verifier();
    return () => { annule = true; };
  }, [transactionId]);

  // Annonce payée : l'annonce elle-même ; abonnement : la rubrique ouverte.
  // (Air-Bengue passe en modération : on renvoie alors vers « Mes annonces ».)
  const cible =
    annonceId && categorie !== "air"
      ? `/annonces/${annonceId}?paye=1`
      : typePaiement === "annonce"
        ? "/annonces?paye=1"
        : `${cibleRubrique(categorie)}?paye=1`;

  // Paiement confirmé : on ramène le membre sur son annonce ou dans sa rubrique.
  useEffect(() => {
    if (etat !== "accepte") return;
    const tic = setInterval(() => setCompteur((c) => c - 1), 1000);
    const redirection = setTimeout(() => navigate(cible, { replace: true }), 1800);
    return () => { clearInterval(tic); clearTimeout(redirection); };
  }, [etat, cible, navigate]);

  const nomRubrique = categorie === "tout" ? "le Pack Tout-Accès" : CATEGORIES[categorie]?.name || "votre rubrique";
  const vues = {
    verification: { Icon: Loader2, spin: true, couleur: "#D4AF37",
      titre: "Vérification du paiement…", texte: "Merci de patienter quelques secondes." },
    accepte: typePaiement === "annonce"
      ? { Icon: CheckCircle2, couleur: "#0A5C36", titre: "Annonce publiée !",
          texte: `Votre annonce est en ligne dans ${nomRubrique}. Ouverture dans ${Math.max(compteur, 1)} seconde(s)…` }
      : { Icon: CheckCircle2, couleur: "#0A5C36", titre: "Paiement confirmé !",
          texte: `Votre accès à ${nomRubrique} est activé. Ouverture dans ${Math.max(compteur, 1)} seconde(s)…` },
    en_attente: { Icon: Clock, couleur: "#D4AF37", titre: "Paiement en cours de confirmation",
      texte: "Votre argent n'est pas perdu : dès que l'opérateur confirme, votre accès s'ouvre automatiquement. Vous pouvez recharger cette page dans une minute." },
    refuse: { Icon: XCircle, couleur: "#E05638", titre: "Paiement non abouti",
      texte: "Le paiement n'a pas été confirmé par l'opérateur. Aucun montant n'a été débité, vous pouvez réessayer." },
    introuvable: { Icon: LifeBuoy, couleur: "#D4AF37", titre: "Paiement en cours de rapprochement",
      texte: "Si vous avez reçu le message de débit de votre opérateur, votre accès sera activé automatiquement. Notez la référence ci-dessous et contactez-nous si rien ne bouge d'ici 30 minutes." },
  };
  const v = vues[etat];

  return (
    <div className="bg-[#FAF8F5] min-h-screen">
      <Navbar />
      <main className="max-w-lg mx-auto px-4 pt-40 pb-24 text-center" data-testid="paiement-retour">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl border border-[#E5DFD5] p-10 shadow-sm"
        >
          <v.Icon size={56} className={`mx-auto mb-5 ${v.spin ? "animate-spin" : ""}`} style={{ color: v.couleur }} />
          <h1 className="font-heading text-2xl font-extrabold text-stone-900" data-testid="paiement-retour-titre">
            {v.titre}
          </h1>
          <p className="mt-3 text-stone-600">{v.texte}</p>

          {transactionId && etat !== "accepte" && (
            <p className="mt-4 rounded-2xl bg-[#FAF8F5] border border-[#E5DFD5] px-4 py-2 text-xs font-semibold tracking-wide text-stone-500 break-all"
               data-testid="paiement-retour-reference">
              Référence : {transactionId}
            </p>
          )}

          <div className="mt-8 flex flex-col gap-3">
            {etat === "accepte" && (
              <>
                <Link
                  to={cible}
                  data-testid="paiement-retour-voir-annonces"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0A5C36] text-white py-3.5 font-semibold transition-transform hover:scale-[1.02]"
                >
                  {annonceId || typePaiement === "annonce" ? "Voir mon annonce en ligne" : `Entrer dans ${nomRubrique}`} <ArrowRight size={16} />
                </Link>
                {typePaiement === "annonce" && (
                  <Link to="/annonces" data-testid="paiement-retour-mes-annonces"
                    className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#0A5C36] py-3 font-semibold text-[#0A5C36]">
                    Mes annonces <ArrowRight size={16} />
                  </Link>
                )}
              </>
            )}
            {(etat === "en_attente" || etat === "introuvable") && (
              <>
                <button
                  onClick={() => window.location.reload()}
                  data-testid="paiement-retour-rafraichir"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0A5C36] text-white py-3.5 font-semibold"
                >
                  Vérifier à nouveau <ArrowRight size={16} />
                </button>
                <Link
                  to={typePaiement === "annonce" ? "/annonces" : cibleRubrique(categorie)}
                  data-testid="paiement-retour-entrer-quand-meme"
                  className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#0A5C36] text-[#0A5C36] py-3 font-semibold"
                >
                  Entrer dans {nomRubrique} <ArrowRight size={16} />
                </Link>
              </>
            )}
            <RetourVuePrecedente libelle="Revenir à ce que je consultais avant de payer"
              className="justify-center py-3" />
            <Link to="/compte" data-testid="paiement-retour-compte"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#12100E] text-white py-3 font-semibold">
              Mon compte <ArrowRight size={16} />
            </Link>
            {etat === "refuse" && (
              <Link to="/abonnement" className="text-sm font-semibold text-[#E05638]">
                Réessayer l'abonnement
              </Link>
            )}
            {etat === "introuvable" && (
              <Link to="/abonnement" className="text-sm font-semibold text-[#E05638]">
                Voir mes abonnements
              </Link>
            )}
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
